'use client';

import { useState, useMemo, Suspense } from 'react';
import { CalendarDays, Search, Users, Heart } from 'lucide-react';
import { isThisMonth, isToday, isThisWeek } from 'date-fns';

import { getNextBirthday } from '@/lib/date-utils';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { BirthdayList } from '@/components/birthday-list';
import { AnniversaryList } from '@/components/anniversary-list';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useNotifications } from '@/hooks/use-notifications.tsx';
import { Skeleton } from '@/components/ui/skeleton';
import { AddBirthday } from '@/components/add-birthday';
import { AddAnniversary } from '@/components/add-anniversary';
import { WeeklyView } from '@/components/weekly-view';
import { YearlyView } from '@/components/yearly-view';
import { BirthdayCountdown } from '@/components/birthday-countdown';
import { AnniversaryCountdown } from '@/components/anniversary-countdown';
import { useBirthdays } from '@/hooks/use-birthdays';
import { useAnniversaries } from '@/hooks/use-anniversaries';

export function Dashboard() {
  // The hooks now fetch data on the client. Initial data is an empty array.
  const { birthdays } = useBirthdays();
  const { anniversaries } = useAnniversaries();

  useNotifications();
  const [searchTerm, setSearchTerm] = useState('');
  const [anniversarySearchTerm, setAnniversarySearchTerm] = useState('');
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(
    new Date()
  );

  const sortedBirthdays = useMemo(() => {
    if (!birthdays) return [];
    return [...birthdays].sort((a, b) => {
      if (!a.dob) return 1;
      if (!b.dob) return -1;
      const nextA = getNextBirthday(a.dob);
      const nextB = getNextBirthday(b.dob);
      if (!nextA) return 1;
      if (!nextB) return -1;
      return nextA.getTime() - nextB.getTime();
    });
  }, [birthdays]);

  const sortedAnniversaries = useMemo(() => {
    if (!anniversaries) return [];
    return [...anniversaries].sort((a, b) => {
      const nextA = getNextBirthday(a.date);
      const nextB = getNextBirthday(b.date);
      if (!nextA || !nextB) return 0;
      return nextA.getTime() - nextB.getTime();
    });
  }, [anniversaries]);

  const nextUpcomingBirthday = useMemo(() => {
    return sortedBirthdays.find((b) => b.dob) || null;
  }, [sortedBirthdays]);

  const nextUpcomingAnniversary = useMemo(() => {
    return sortedAnniversaries.find((a) => a.date) || null;
  }, [sortedAnniversaries]);

  const filteredBirthdays = useMemo(() => {
    return sortedBirthdays.filter((birthday) =>
      birthday.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [sortedBirthdays, searchTerm]);

  const filteredAnniversaries = useMemo(() => {
    return sortedAnniversaries.filter((anniversary) =>
      anniversary.name
        .toLowerCase()
        .includes(anniversarySearchTerm.toLowerCase())
    );
  }, [sortedAnniversaries, anniversarySearchTerm]);

  const todayBirthdays = useMemo(() => {
    return filteredBirthdays.filter((b) => {
      if (!b.dob) return false;
      const nextBirthday = getNextBirthday(b.dob);
      return nextBirthday ? isToday(nextBirthday) : false;
    });
  }, [filteredBirthdays]);

  const thisWeekBirthdays = useMemo(() => {
    return filteredBirthdays.filter((b) => {
      if (!b.dob) return false;
      const nextBirthday = getNextBirthday(b.dob);
      return nextBirthday
        ? isThisWeek(nextBirthday, { weekStartsOn: 1 })
        : false;
    });
  }, [filteredBirthdays]);

  const thisMonthBirthdays = useMemo(() => {
    return filteredBirthdays.filter((b) => {
      if (!b.dob) return false;
      const nextBirthday = getNextBirthday(b.dob);
      return nextBirthday ? isThisMonth(nextBirthday) : false;
    });
  }, [filteredBirthdays]);

  return (
    <Tabs
      defaultValue="birthdays"
      className="w-full"
    >
      <TabsList className="grid w-full grid-cols-2">
        <TabsTrigger value="birthdays">Birthdays</TabsTrigger>
        <TabsTrigger value="anniversaries">Anniversaries</TabsTrigger>
      </TabsList>
      <TabsContent value="birthdays" className="mt-8">
        <div className="grid gap-8">
          <div className="grid grid-cols-1 gap-8 items-start">
            {nextUpcomingBirthday && (
              <Suspense fallback={<Skeleton className="h-48" />}>
                <BirthdayCountdown birthday={nextUpcomingBirthday} />
              </Suspense>
            )}
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Today's Birthdays
                </CardTitle>
                <CalendarDays className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {todayBirthdays.length}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">This Week</CardTitle>
                <CalendarDays className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {thisWeekBirthdays.length}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  This Month
                </CardTitle>
                <CalendarDays className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {thisMonthBirthdays.length}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Total Birthdays
                </CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{birthdays.length}</div>
              </CardContent>
            </Card>
          </div>

          <div className="flex justify-between items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Search by name..."
                className="w-full pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <AddBirthday />
          </div>

          <Tabs defaultValue="upcoming">
            <TabsList>
              <TabsTrigger value="upcoming">Upcoming</TabsTrigger>
              <TabsTrigger value="this-month">This Month</TabsTrigger>
              <TabsTrigger value="week">Week</TabsTrigger>
              <TabsTrigger value="year">Year</TabsTrigger>
            </TabsList>
            <TabsContent value="upcoming">
              <BirthdayList
                birthdays={filteredBirthdays}
                title="Upcoming Birthdays"
                emptyMessage="No upcoming birthdays found. Add one to get started!"
              />
            </TabsContent>
            <TabsContent value="this-month">
              <BirthdayList
                birthdays={thisMonthBirthdays}
                title="This Month's Birthdays"
                emptyMessage="No birthdays this month."
              />
            </TabsContent>
            <TabsContent value="week">
              <Suspense fallback={<Skeleton className="h-[500px] w-full" />}>
                <WeeklyView
                  birthdays={birthdays}
                  selectedDate={selectedDate}
                  onDateSelect={setSelectedDate}
                />
              </Suspense>
            </TabsContent>
            <TabsContent value="year">
              <Suspense fallback={<Skeleton className="h-[800px] w-full" />}>
                <YearlyView birthdays={birthdays} />
              </Suspense>
            </TabsContent>
          </Tabs>
        </div>
      </TabsContent>
      <TabsContent value="anniversaries" className="mt-8">
        <div className="grid gap-8">
          {nextUpcomingAnniversary && (
            <Suspense fallback={<Skeleton className="h-48" />}>
              <AnniversaryCountdown anniversary={nextUpcomingAnniversary} />
            </Suspense>
          )}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Total Anniversaries
                </CardTitle>
                <Heart className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {anniversaries.length}
                </div>
              </CardContent>
            </Card>
          </div>
          <div className="flex justify-between items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="search"                placeholder="Search by name..."
                className="w-full pl-10"
                value={anniversarySearchTerm}
                onChange={(e) => setAnniversarySearchTerm(e.target.value)}
              />
            </div>
            <AddAnniversary />
          </div>
          <AnniversaryList
            anniversaries={filteredAnniversaries}
            title="All Anniversaries"
            emptyMessage="No anniversaries found. Add one to get started!"
          />
        </div>
      </TabsContent>
    </Tabs>
  );
}
